import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { translate } from '../../Services/translation';
import {
  setCustomerDetails,
  setDropshipCustomerDetails,
  setAlternativeAddress,
  setShowAlternativeAddress,
  setSignUp,
  setCountry,
} from '../../Actions/Checkout.action';
import {
  dropshipValidationSchema,
  privateCustomerAddressSchema,
} from './utils/Checkout.ValidationSchema';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import Dropdown from '../Dropdown';
import Checkbox from '../Inputs/Checkbox';
import CheckoutCustomerInfoInput from './utils/Checkout.CustomerInfoInput';
import {
  CHECKOUT_DROPSHIP_COMPANY,
  CHECKOUT_DROPSHIP_PRIVATE,
} from '../../constants';
import { StateProps } from '../../Utils/states';
import Listbox from '../Listbox';

const CheckoutCustomerInfoForm = ({
  showSignUp,
  allowAlternativeAddress = true,
}: {
  showSignUp: boolean;
  allowAlternativeAddress?: boolean;
}) => {
  const dispatch = useDispatch();
  const [languagePickerOpen, setLanguagePickerOpen] = useState(false);
  const { payload } = useTypedSelector((state) => state.checkout);
  const {
    customerDetails = {},
    signUp,
    showAlternativeAddress,
    useDropshipping,
    selectedDropshipAddressType = CHECKOUT_DROPSHIP_PRIVATE,
    dropshipCustomerDetails,
  } = payload;
  const [availableStates, setAvailableStates] = useState<
    StateProps[] | undefined
  >();

  const onCustomerDetailsChange = useCallback(
    (stateKey, id, value) => {
      switch (stateKey) {
        case 'customerDetails':
          dispatch(setCustomerDetails(id, value));
          break;
        case 'dropshipCustomerDetails':
          dispatch(setDropshipCustomerDetails(id, value));
          break;
        case 'alternativeAddress':
          dispatch(setAlternativeAddress(id, value));
          break;
      }
    },
    [dispatch]
  );

  const stateRequired = useMemo(
    () =>
      customerDetails?.country == 'US' ||
      customerDetails?.country == 'CA' ||
      dropshipCustomerDetails?.country == 'US' ||
      dropshipCustomerDetails?.country == 'CA',
    [customerDetails?.country, dropshipCustomerDetails?.country]
  );

  useEffect(() => {
    const importStates = async () => {
      if (
        !stateRequired ||
        (!customerDetails?.country && !dropshipCustomerDetails?.country)
      ) {
        setAvailableStates(undefined);
        return;
      }
      // Async import to not increase bundle size
      const { getCountryByIso2 } = await import('../../Utils/states');
      const states =
        dropshipCustomerDetails?.country == 'US' ||
        dropshipCustomerDetails?.country == 'CA'
          ? getCountryByIso2(dropshipCustomerDetails.country)?.states
          : getCountryByIso2(customerDetails.country)?.states;
      setAvailableStates(states as StateProps[] | undefined);
    };
    importStates();
  }, [
    stateRequired,
    customerDetails?.country,
    dropshipCustomerDetails?.country,
  ]);

  const countryOptionList = useMemo(
    () =>
      global['LitiumConstants'].countries.map(({ text, value }) => ({
        label: text,
        value,
        selected:
          (useDropshipping
            ? dropshipCustomerDetails?.country
            : customerDetails.country) == value,
        disabled:
          (useDropshipping
            ? dropshipCustomerDetails?.country
            : customerDetails.country) == value,
      })),
    [
      useDropshipping,
      dropshipCustomerDetails?.country,
      customerDetails.country,
    ]
  );

  return (
    <>
      <form className="mt-6 grid grid-cols-2 gap-x-4">
        <h4 className="col-span-2 mb-3">
          {translate('checkout.customerinfo.contact-title')}
        </h4>
        {useDropshipping && (
          <>
            <CheckoutCustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="dropshipCustomerDetails"
              id="firstName"
              autoComplete="given-name"
              name="fname"
              validationSchema={dropshipValidationSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CheckoutCustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="dropshipCustomerDetails"
              id="lastName"
              autoComplete="family-name"
              name="lname"
              validationSchema={dropshipValidationSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
          </>
        )}
        <CheckoutCustomerInfoInput
          wrapperClassName="col-span-1"
          stateKey={
            useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
          }
          id="phoneNumber"
          validationSchema={
            useDropshipping
              ? dropshipValidationSchema
              : privateCustomerAddressSchema
          }
          onCustomerDetailsChange={onCustomerDetailsChange}
        />
        <CheckoutCustomerInfoInput
          wrapperClassName="col-span-1"
          stateKey={
            useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
          }
          id="email"
          validationSchema={
            useDropshipping
              ? dropshipValidationSchema
              : privateCustomerAddressSchema
          }
          onCustomerDetailsChange={onCustomerDetailsChange}
        />
        <h4 className="col-span-2 mb-3">
          {translate('checkout.customerinfo.address-title')}
        </h4>
        <div className="relative col-span-2 mb-4">
          <Dropdown
            hideForDesktop={true}
            selectedValue={
              useDropshipping
                ? dropshipCustomerDetails?.country
                : customerDetails.country
            }
            options={global['LitiumConstants'].countries.map(
              ({ text, value }) => ({ label: text, value })
            )}
            onChange={({ value }) => {
              !useDropshipping && dispatch(setCountry(value));
              onCustomerDetailsChange(
                'alternativeAddress',
                'country',
                value
              );
              onCustomerDetailsChange(
                useDropshipping
                  ? 'dropshipCustomerDetails'
                  : 'customerDetails',
                'country',
                value
              );
            }}
          />
          <Listbox
            continerClassName="hidden lg:block"
            isOpen={languagePickerOpen}
            setIsOpen={setLanguagePickerOpen}
            inputSize="large"
            label={translate('checkout.customerinfo.country')}
            setSelectedOption={(option) => {
              if (!useDropshipping) {
                dispatch(setCountry(option.value));
                onCustomerDetailsChange(
                  'alternativeAddress',
                  'country',
                  option.value
                );
              }
              onCustomerDetailsChange(
                useDropshipping
                  ? 'dropshipCustomerDetails'
                  : 'customerDetails',
                'country',
                option.value
              );
            }}
            optionList={countryOptionList}
            selectedOption={countryOptionList.find(
              (o) =>
                o.value ==
                (useDropshipping
                  ? dropshipCustomerDetails?.country
                  : customerDetails.country)
            )}
          />
          <label
            className="absolute -top-2 left-[14px] bg-white px-1 text-sm font-light text-gray-700"
            htmlFor="country"
          >
            {translate('checkout.customerinfo.country')}
          </label>
        </div>
        {!useDropshipping && (
          <>
            <CheckoutCustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="customerDetails"
              id="firstName"
              autoComplete="given-name"
              name="fname"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
            <CheckoutCustomerInfoInput
              wrapperClassName="col-span-1"
              stateKey="customerDetails"
              id="lastName"
              autoComplete="family-name"
              name="lname"
              validationSchema={privateCustomerAddressSchema}
              onCustomerDetailsChange={onCustomerDetailsChange}
            />
          </>
        )}
        {selectedDropshipAddressType == CHECKOUT_DROPSHIP_COMPANY && (
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey={
              useDropshipping
                ? 'dropshipCustomerDetails'
                : 'customerDetails'
            }
            id="companyName"
            autoComplete="company-name"
            validationSchema={
              useDropshipping
                ? dropshipValidationSchema
                : privateCustomerAddressSchema
            }
            onCustomerDetailsChange={onCustomerDetailsChange}
            required={true}
          />
        )}
        <CheckoutCustomerInfoInput
          wrapperClassName="col-span-2"
          stateKey={
            useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
          }
          id="address"
          autoComplete="address-line1"
          validationSchema={
            useDropshipping
              ? dropshipValidationSchema
              : privateCustomerAddressSchema
          }
          onCustomerDetailsChange={onCustomerDetailsChange}
        />
        <CheckoutCustomerInfoInput
          wrapperClassName="col-span-2"
          stateKey={
            useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
          }
          id="address2"
          validationSchema={
            useDropshipping
              ? dropshipValidationSchema
              : privateCustomerAddressSchema
          }
          onCustomerDetailsChange={onCustomerDetailsChange}
        />
        <CheckoutCustomerInfoInput
          wrapperClassName="col-span-1"
          stateKey={
            useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
          }
          id="zipCode"
          validationSchema={
            useDropshipping
              ? dropshipValidationSchema
              : privateCustomerAddressSchema
          }
          onCustomerDetailsChange={onCustomerDetailsChange}
        />
        <CheckoutCustomerInfoInput
          wrapperClassName="col-span-1"
          stateKey={
            useDropshipping ? 'dropshipCustomerDetails' : 'customerDetails'
          }
          id="city"
          validationSchema={
            useDropshipping
              ? dropshipValidationSchema
              : privateCustomerAddressSchema
          }
          onCustomerDetailsChange={onCustomerDetailsChange}
        />

        {stateRequired && availableStates && (
          <div className="relative col-span-2 mb-4">
            <Dropdown
              selectedValue={
                useDropshipping
                  ? dropshipCustomerDetails?.state
                  : customerDetails?.state
              }
              options={availableStates.map(({ name, state_code }) => ({
                label: name,
                value: state_code,
              }))}
              onChange={({ value }) => {
                onCustomerDetailsChange(
                  useDropshipping
                    ? 'dropshipCustomerDetails'
                    : 'customerDetails',
                  'state',
                  value
                );
              }}
            />
            <label
              className="absolute -top-2 left-[14px] bg-white px-1 text-sm font-light text-gray-700"
              htmlFor="state"
            >
              {translate('checkout.customerinfo.state')}
            </label>
          </div>
        )}
      </form>
      <div className="flex flex-col gap-4">
        {showSignUp && (
          <Checkbox
            id="signupandlogin"
            inputSize="large"
            checked={signUp}
            label={translate('checkout.customerinfo.signupandlogin')}
            onChange={(e) => dispatch(setSignUp(e.target.checked))}
          />
        )}
        {allowAlternativeAddress && (
          <Checkbox
            id="showAlternativeAddress"
            inputSize="large"
            checked={showAlternativeAddress}
            label={translate(
              'checkout.customerinfo.showAlternativeAddress'
            )}
            onChange={(e) =>
              dispatch(setShowAlternativeAddress(e.target.checked))
            }
          />
        )}
      </div>
      {allowAlternativeAddress && showAlternativeAddress && (
        <form className="mt-4">
          <h4 className="col-span-2 mb-3">
            {translate('checkout.alternative-address')}
          </h4>
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="firstName"
            autoComplete="given-name"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="lastName"
            autoComplete="family-name"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="alternativeAddress"
            id="address"
            autoComplete="address-line1"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-2"
            stateKey="alternativeAddress"
            id="address2"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="zipCode"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          <CheckoutCustomerInfoInput
            wrapperClassName="col-span-1"
            stateKey="alternativeAddress"
            id="city"
            validationSchema={privateCustomerAddressSchema}
            onCustomerDetailsChange={onCustomerDetailsChange}
          />
          {stateRequired && availableStates && (
            <div className="relative col-span-2 mb-4">
              <Dropdown
                selectedValue={customerDetails.state}
                options={availableStates.map(({ name, state_code }) => ({
                  label: name,
                  value: state_code,
                }))}
                onChange={({ value }) => {
                  onCustomerDetailsChange(
                    'alternativeAddress',
                    'state',
                    value
                  );
                }}
              />
              <label
                className="absolute -top-2 left-[14px] bg-white px-1 text-sm font-light text-gray-700"
                htmlFor="state"
              >
                {translate('checkout.customerinfo.state')}
              </label>
            </div>
          )}
        </form>
      )}
    </>
  );
};

export default CheckoutCustomerInfoForm;
